import React, { useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useDocument } from '@nandorojo/swr-firestore';
import { SettingCollectionPath } from '@typings/collections.enum';
import { IgameUiMenuSetting } from '@typings/setting/gameUiMenuSetting';
import { IBoardSet } from '@typings/menu/boardSetType';
import GameUiGridSystem from '@template/CustomGridSystem/GameUiGridSystem';
import {
  MENU_TEXT_TYPE,
  MENU_TEXT_TYPE_VALUE,
} from '@typings/menu/menuTextType';
import {
  MENU_ITEM_TYPE,
  MENU_ITEM_TYPE_VALUE,
} from '@typings/menu/menuItemType';
import { GameUIMenuItem, GameUIMenuImgWrapper } from '../common/GameUIMenuItem';
import {
  NavIcon,
  imageAlignmentStyles,
} from '@components/navigation/common/commonStyle';
import { sanitizeGridAreaName } from '@utils/sanitizeGridAreaName';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { homeSettingState } from '@atom/setting/homeSettingState';
import { boardSetListState } from '@atom/setting/boardSetListState';
import { BOARD_TYPE } from '@typings/boardType';
import scrollTop from '@hooks/resetScrollTop';
import { cookieStore } from '@store/cookie/cookieStore';

const GameUINavContainer = styled.div`
  width: 100%;
  height: auto;
  /* 📦 컨테이너 쿼리 활성화 */
  container-type: inline-size;
  container-name: gameui;
`;

const EmptySlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: var(--cardBackgroundColor);
  border: 2px dashed var(--lineColor);
  border-radius: 8px;
  color: var(--subTextColor);
  font-size: 12px;
  opacity: 0.6;

  /* 📍 컨테이너 쿼리 (명명된 컨테이너 사용) */
  @container gameui (max-width: 200px) {
    padding: 8px;
    font-size: 10px;
  }
`;

interface GameUiMainNavListProps {
  containerMaxWidth?: string;
}

function GameUiMainNavList({ containerMaxWidth }: GameUiMainNavListProps = {}) {
  const navigate = useNavigate();

  // GameUI 설정 데이터 가져오기
  const { data: gameUiData } = useDocument<IgameUiMenuSetting>(
    `${SettingCollectionPath.GAMEUI}`,
    { listen: true },
  );

  // Homepage 설정 데이터 가져오기 (전역 메뉴 설정용)
  // 📍 중앙화된 홈페이지 설정 사용 (기존 useDocument 대체)
  const homeSet = useRecoilValue(homeSettingState) as any;
  const useImgShadow = homeSet?.menu?.useImgShadow ?? false;
  const imgShadowColor = homeSet?.menu?.imgShadowColor ?? 'rgba(0,0,0,0.35)';

  // 보드셋 리스트 가져오기
  const boardSetList = useRecoilValue(boardSetListState);

  // Homepage 설정에서 전역 메뉴 설정 가져오기
  const globalMenuItemType: MENU_ITEM_TYPE_VALUE =
    homeSet?.menu?.menuItemType || MENU_ITEM_TYPE.IMAGE;
  const globalMenuTextType: MENU_TEXT_TYPE_VALUE =
    homeSet?.menu?.menuTextType || MENU_TEXT_TYPE.CENTER;

  // 메뉴 클릭 핸들러
  const handleMenuClick = (boardItem: IBoardSet | undefined) => {
    if (!boardItem?.path) return;

    // 커스텀메뉴형(LINK)인 경우 완전한 URL로 처리
    if (boardItem.type === BOARD_TYPE.LINK) {
      const getCustomLink = () => {
        const path = boardItem.path;
        if (!path) return window.location.origin;

        // http:// 또는 https://로 시작하면 외부 URL로 처리
        if (path.startsWith('http://') || path.startsWith('https://')) {
          return path;
        }

        // '/'로 시작하는 경로는 현재 origin + 해당 경로
        if (path.startsWith('/')) {
          return `${window.location.origin}${path}`;
        }

        // 그 외는 현재 홈페이지 주소 뒤에 붙이기
        return `${window.location.origin}/${path}`;
      };

      const customLink = getCustomLink();
      if (boardItem.openNewTab) {
        window.open(customLink, '_blank', 'noreferrer');
      } else {
        window.location.href = customLink;
        scrollTop();
        cookieStore.setOffset('0');
      }
    } else {
      // 일반 메뉴는 React Router로 내부 네비게이션
      navigate(`/${boardItem.path}`);
      scrollTop();
      cookieStore.setOffset('0');
    }
  };

  // 렌더링 컴포넌트 맵 생성 (단순화된 구조)
  const renderComponentMap: Record<
    string,
    (props: { key: React.Key }) => JSX.Element
  > = useMemo(() => {
    if (!gameUiData?.grid || !boardSetList) {
      return {};
    }

    const componentMap: Record<
      string,
      (props: { key: React.Key }) => JSX.Element
    > = {};

    // gameUiData.grid의 각 status에 대해서만 컴포넌트 생성
    Object.values(gameUiData.grid).forEach((gridItem) => {
      const { status } = gridItem;
      const gridAreaName = sanitizeGridAreaName(status);

      componentMap[status] = ({ key }) => {
        let targetBoard: IBoardSet | undefined;

        // status가 'empty'가 아닌 경우, 보드 ID로 직접 매핑
        if (status !== 'empty') {
          targetBoard = boardSetList.find((board) => board.id === status);
        }

        if (targetBoard) {
          const { title, imgURL } = targetBoard;

          // 타입별 직접 렌더링 (wrapper 없이)
          if (globalMenuItemType === MENU_ITEM_TYPE.IMAGE) {
            const hoverImg = (targetBoard as any)?.hoverImgURL || '';
            const mobileImg = (targetBoard as any)?.mobileImgURL || '';
            const isMobile =
              typeof window !== 'undefined' &&
              window.matchMedia &&
              window.matchMedia('(max-width: 767px)').matches;
            const currentImg = isMobile && mobileImg ? mobileImg : imgURL;
            const hasHover = !!hoverImg && !isMobile;

            return (
              <GameUIMenuImgWrapper
                key={key}
                style={{ gridArea: gridAreaName }}
                onClick={() => handleMenuClick(targetBoard)}
                $useShadow={useImgShadow}
                $shadowColor={imgShadowColor}
              >
                <NavIcon
                  style={{
                    ...imageAlignmentStyles[globalMenuTextType],
                    width: '100%',
                    display: 'inline-flex',
                  }}
                  $hasHover={hasHover}
                  $disableMaxHeight
                >
                  <img
                    className="base-img"
                    src={currentImg}
                    alt={title}
                    loading="lazy"
                  />
                  {hasHover && (
                    <img
                      className="hover-img"
                      src={hoverImg}
                      alt={`${title}-hover`}
                      loading="lazy"
                    />
                  )}
                </NavIcon>
              </GameUIMenuImgWrapper>
            );
          } else {
            return (
              <GameUIMenuItem
                key={key}
                style={{
                  gridArea: gridAreaName,
                  textAlign: globalMenuTextType,
                }}
                onClick={() => handleMenuClick(targetBoard)}
              >
                {title}
              </GameUIMenuItem>
            );
          }
        }

        // 빈 슬롯
        return (
          <EmptySlot key={key} style={{ gridArea: gridAreaName }}>
            <div style={{ textAlign: 'center' }}>
              <div>
                {status === 'empty' ? '빈 슬롯' : '메뉴를 설정해주세요'}
              </div>
              {status !== 'empty' && (
                <div
                  style={{ fontSize: '10px', marginTop: '4px', opacity: 0.7 }}
                >
                  ID: {status}
                </div>
              )}
            </div>
          </EmptySlot>
        );
      };
    });

    return componentMap;
  }, [gameUiData?.grid, boardSetList, globalMenuItemType, globalMenuTextType]);

  // 그리드 데이터가 없으면 설정 안내 표시
  if (!gameUiData?.grid) {
    return (
      <GameUINavContainer>
        <EmptySlot>
          <p>GameUI 메뉴를 설정해주세요</p>
          <p style={{ fontSize: '12px', marginTop: '8px' }}>
            설정 → 홈페이지 설정 → 메뉴 레이아웃 → 게임 UI 설정
          </p>
        </EmptySlot>
      </GameUINavContainer>
    );
  }

  return (
    <GameUINavContainer>
      <GameUiGridSystem
        customGrid={gameUiData.grid}
        customGridSize={gameUiData.gridSize}
        renderComponentMap={renderComponentMap}
        containerStyle={{
          minHeight: '330px',
          maxHeight: 'none',
          // 📍 max-width 제약 조건을 containerStyle로 전달
          maxWidth: containerMaxWidth || '100%',
          gap:
            homeSet?.menu?.menuGap ||
            (globalMenuItemType === MENU_ITEM_TYPE.IMAGE ? '0' : '8px'),
        }}
      />
    </GameUINavContainer>
  );
}

export default GameUiMainNavList;
